/**
 * Builds `items_ticketing.token_payload` the same way the ticket forms do
 * (TicketingItemCreate/Edit/Clone actions), so tickets saved from the setup
 * page mint identical token names.
 */
import type { POSToken } from "../../../types/tickets";

export interface TokenPayloadEntry {
  balance: number;
  redeemable_token_id: string;
  redeemable_token_name: string;
}

export interface TokenPayloadInput {
  name: string;
  isTopUp: boolean;
  unlimitedIngress: boolean;
  includeEgress: boolean;
  includePosTokens: boolean;
  posTokens: POSToken[];
}

export const EGRESS_SUFFIX = " - Egress";

export function buildTokenPayload(input: TokenPayloadInput): Record<string, TokenPayloadEntry> {
  const name = input.name.trim();
  const payload: Record<string, TokenPayloadEntry> = {};
  if (!input.isTopUp && name) {
    payload[name] = {
      balance: input.unlimitedIngress ? 10000 : 1,
      redeemable_token_id: name,
      redeemable_token_name: name,
    };
  }
  if (input.includeEgress && name) {
    const egress = name + EGRESS_SUFFIX;
    payload[egress] = { balance: 10000, redeemable_token_id: egress, redeemable_token_name: egress };
  }
  if (input.includePosTokens) {
    for (const t of input.posTokens || []) {
      const n = String(t?.name ?? "").trim();
      if (!n) continue;
      payload[n] = { balance: Number(t.quantity) || 0, redeemable_token_id: n, redeemable_token_name: n };
    }
  }
  return payload;
}

/** Plain-words summary of what a ticket grants, for the tickets table. */
export function describeGrants(t: {
  cashless_top_up_item?: boolean;
  unlimited_ingress?: boolean;
  include_egress?: boolean;
  include_pos_tokens?: boolean;
  pos_token_payload?: POSToken[] | null;
  included_cash_balance?: number | null;
  included_promo_balance?: number | null;
}): string {
  const parts: string[] = [];
  if (!t.cashless_top_up_item) parts.push(t.unlimited_ingress ? "Unlimited entry" : "1 entry scan");
  if (t.include_egress) parts.push("exit scans");
  if (t.include_pos_tokens) {
    const tokens = (t.pos_token_payload || []).filter((p) => p?.name && Number(p.quantity) > 0);
    if (tokens.length) parts.push(tokens.map((p) => `${p.quantity} ${p.name}`).join(", "));
  }
  const cash = Number(t.included_cash_balance) || 0;
  const promo = Number(t.included_promo_balance) || 0;
  if (cash) parts.push(`$${(cash / 100).toFixed(2)} cash balance`);
  if (promo) parts.push(`$${(promo / 100).toFixed(2)} promo balance`);
  return parts.length ? parts.join(", ") : "nothing (check the settings)";
}
